"use client";

// A BACK OR FORWARD PRESSED BEFORE THE PAGE HYDRATED IS KEPT, NOT UNDONE.
//
// THE BUG (Next 16.3, found by tests/e2e/schedule-scope-bar.spec.ts, two CI
// sightings). Reload a page that
// was reached by a client navigation, then press Back while the reloaded
// document is still hydrating. The two entries are one document, so Back is a
// same-document traversal: the address changes and `popstate` fires — but
// Next's router has not mounted, so nobody is listening, and nothing renders
// the entry Back arrived at. Then Next hydrates, and its HistoryUpdater
// (next/dist/client/components/app-router.js) writes the URL it was RENDERED
// for over the current entry with `history.replaceState`. The address goes
// back to where it was, Back looks like it did nothing, and the earlier entry
// is overwritten for good: the page a person reloaded is now in the history
// twice and the one before it is gone. A real person hits it by pressing Back
// in the second or so a reload takes to hydrate — longer on a slow phone.
//
// THE FIX, IN TWO HALVES:
//
//   BackBeforeHydrationScript, an inline script in <head>, runs as the
//   document is parsed — before any of Next's chunks — and remembers the
//   address of every `popstate` until the app takes over.
//   BackBeforeHydration, mounted at the root, runs once the app has hydrated:
//   it stops listening, and if a traversal arrived somewhere other than the
//   address the app RENDERED, it NAVIGATES there by `router.replace` — the
//   entry becomes that address again, rendered under it (never
//   `history.replaceState`: Next 16 cannot go Back to an entry whose address
//   and render disagree, lens-url-sync.tsx).
//
// ⚠️ AGAINST THE RENDERED ADDRESS, NOT THE CURRENT ONE. A Back can land in two
// places relative to hydration, and both lose it: BEFORE Next's HistoryUpdater
// (an insertion effect, in the commit) — which then writes the rendered URL
// over it, so the address is wrong too; or AFTER it, before the passive effects
// that register Next's `popstate` listener — which leaves the address right and
// the page still showing the reloaded one (measured on the built image at 4×
// CPU: both happen). The address the last `popstate` arrived at is the answer
// either way; what was rendered is `usePathname` + `useSearchParams`.
//
// There is no gap after this half: passive effects are flushed in one task,
// children before parents, so Next's own listener (the root router's effect)
// is registered before any traversal can land after this component's look.
//
// It also marks the document hydrated (`<html data-hydrated>`): the signal a
// test waits on before it clicks, instead of retrying a click on a page that
// had not hydrated.

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAppRouter } from "@/lib/app-router";

declare global {
  interface Window {
    /** The address the last pre-hydration `popstate` arrived at, or null. */
    __popBeforeHydration?: string | null;
    /** Stops the pre-hydration listener. */
    __stopPopBeforeHydration?: () => void;
  }
}

const SOURCE = `(function(){var w=window;w.__popBeforeHydration=null;function f(){w.__popBeforeHydration=location.pathname+location.search+location.hash}addEventListener("popstate",f);w.__stopPopBeforeHydration=function(){removeEventListener("popstate",f)}})();`;

/** The `<head>` half: rendered by the root layout before anything else. */
export function BackBeforeHydrationScript() {
  return <script dangerouslySetInnerHTML={{ __html: SOURCE }} />;
}

/**
 * Where to go once hydrated, or null: the address a pre-hydration traversal
 * arrived at, when the app rendered another (path and query; a hash alone
 * renders nothing different).
 */
export function addressToRestore(popped: string | null | undefined, rendered: string): string | null {
  if (!popped) return null;
  const [path, rest = ""] = popped.split("#")[0].split("?");
  // The query as useSearchParams spells it, so `:` and `%3A` are one address.
  const query = new URLSearchParams(rest).toString();
  return `${path}${query ? `?${query}` : ""}` === rendered ? null : popped;
}

/** The root half: once hydrated, go where a pre-hydration Back or Forward arrived. */
export function BackBeforeHydration() {
  const router = useAppRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const query = params?.toString() ?? "";
  const rendered = `${pathname}${query ? `?${query}` : ""}`;
  useEffect(() => {
    const popped = window.__popBeforeHydration;
    window.__stopPopBeforeHydration?.();
    window.__popBeforeHydration = null;
    document.documentElement.dataset.hydrated = "1";
    const to = addressToRestore(popped, rendered);
    if (to !== null) router.replace(to, { scroll: false });
    // Once, at hydration: later renders are Next's to route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
